import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { PropsWithChildren, useCallback, useMemo } from "react";
import { defaultAuthState, IOidcContext, OidcContext, useAuth } from "..";
import { getErrorWithMessage } from "../../helper-functions";
import { useGenericContext, useAxios } from "../../hooks";
import {
    Credentials,
    DecodedAccessToken,
    ExternalLoginRequest,
    LoginMfaRequest,
    LoginResponse,
    LoginRequest,
    LoginProvider,
    TokenDto,
    MfaCodeRequest,
} from "../../models";
import { Endpoints } from "../../api";

export const OidcProvider = (props: PropsWithChildren) => {
    const navigate = useNavigate();
    const {
        accessToken,
        refreshToken,
        setAuthState,
        getMfaToken,
        setMfaToken,
        deleteMfaToken,
        authState: { loginCredentials },
        setIsPublicComputer,
    } = useAuth();
    const { get, post } = useAxios();

    const logout = useCallback(async () => {
        setAuthState(defaultAuthState);
        setIsPublicComputer(true);
        navigate("/");
    }, [navigate, setAuthState, setIsPublicComputer]);

    const loginFail = useCallback(async () => {
        setAuthState((prev) => ({
            ...defaultAuthState,
            failedLoginAttempts: prev.failedLoginAttempts + 1,
        }));
        setIsPublicComputer(true);
        logout();
    }, [logout, setAuthState, setIsPublicComputer]);

    const saveTokens = useCallback(
        (tokens: TokenDto) => {
            const { accessToken, refreshToken } = tokens;
            const decodedAccessToken = jwtDecode<DecodedAccessToken>(accessToken);
            setAuthState({
                ...defaultAuthState,
                accessToken,
                decodedAccessToken,
                isAuthenticated: true,
                refreshToken,
            });
        },
        [setAuthState]
    );

    const loginMFA = useCallback(
        async (loginMFARequest: LoginMfaRequest): Promise<LoginResponse> => {
            loginMFARequest.password = loginCredentials?.password;
            try {
                setAuthState((prev) => ({ ...prev, isLoggingIn: true }));
                const response = await post<LoginMfaRequest, LoginResponse>({
                    url: "auth/loginmfa",
                    data: loginMFARequest,
                });
                if (response.errorMessage) {
                    return response;
                }
                if (!loginMFARequest.rememberMe) {
                    deleteMfaToken(loginMFARequest.email);
                } else if (response.mfaToken) {
                    setMfaToken({
                        email: loginMFARequest.email,
                        mfaToken: response.mfaToken,
                    });
                }
                if (response.tokens) {
                    saveTokens(response.tokens);
                }
                return response;
            } catch (error) {
                setAuthState((prev) => ({ ...prev, isLoggingIn: false }));
                throw error;
            }
        },
        [loginCredentials, setAuthState, post, deleteMfaToken, setMfaToken, saveTokens]
    );

    const loginMFANewCode = useCallback(
        (mfaCodeRequest: MfaCodeRequest) => {
            return post<MfaCodeRequest, void>({ url: "auth/loginmfanewcode", data: mfaCodeRequest });
        },
        [post]
    );

    const loginWithPassword = useCallback(
        async (data: Credentials): Promise<void> => {
            try {
                setAuthState((prev) => ({ ...prev, isLoggingIn: true }));
                const response = await post<Credentials, TokenDto>({
                    url: "auth/login",
                    data,
                });
                saveTokens(response);
            } catch (error) {
                loginFail();
                throw error;
            }
        },
        [loginFail, post, saveTokens, setAuthState]
    );

    const loginMFARequest = useCallback(
        async (loginRequest: LoginRequest) => {
            try {
                const mfaToken = getMfaToken(loginRequest.email);
                loginRequest.mfaToken = mfaToken?.mfaToken;

                setAuthState((prev) => ({ ...prev, isLoggingIn: true }));
                const response = await post<LoginRequest, LoginResponse>({
                    url: "auth/loginmfarequest",
                    data: loginRequest,
                });

                if (!response) return;

                if (response.tokens?.accessToken) {
                    saveTokens(response.tokens);
                    return;
                }

                setAuthState({
                    ...defaultAuthState,
                    loginMFAResponse: response,
                    loginCredentials: loginRequest,
                });

                if (response.mfaEnabled) {
                    if (response.mfaSetupComplete) {
                        navigate("/mfa");
                    } else {
                        const mfaSetupProps = {
                            subjectId: response.subjectId,
                            emailAddress: response.email,
                            qr_code_image: response.authenticatorQRCode.qrCodeImageUrl,
                        };
                        navigate("/mfasetup", { state: mfaSetupProps });
                    }
                } else {
                    await loginWithPassword(loginRequest);
                }
            } catch (e: any) {
                setAuthState(defaultAuthState);
                throw e;
            }
        },
        [getMfaToken, loginWithPassword, navigate, post, saveTokens, setAuthState]
    );

    const loginWithExternalProvider = useCallback(
        async (data: ExternalLoginRequest) => {
            try {
                setAuthState((prev) => ({ ...prev, isLoggingIn: true }));
                const response = await post<ExternalLoginRequest, TokenDto>({
                    url: "auth/login/external",
                    data,
                });
                saveTokens(response);
            } catch (error) {
                loginFail();
                throw error;
            }
        },
        [loginFail, post, saveTokens, setAuthState]
    );

    const getLoginProviders = useCallback(() => {
        return get<LoginProvider[]>({ url: "loginproviders" });
    }, [get]);

    const refresh = useCallback(async () => {
        if (!refreshToken) return;
        try {
            setAuthState((prev) => ({ ...prev, isRefreshing: true }));
            const tokenDto: TokenDto = {
                accessToken,
                refreshToken,
            };
            const tokens = await post<TokenDto>({ url: Endpoints.Refresh, data: tokenDto });
            if (tokens) {
                const { accessToken, refreshToken } = tokens;
                const { exp, tenant_id, user_id } = jwtDecode<DecodedAccessToken>(accessToken);
                setAuthState((prev) => ({
                    ...prev,
                    accessToken,
                    decodedAccessToken: {
                        ...prev.decodedAccessToken,
                        exp,
                        tenant_id,
                        user_id,
                    },
                    refreshToken,
                    isRefreshing: false,
                }));
            }
        } catch (error) {
            const { message } = getErrorWithMessage(error);
            console.log({ error, message });
            loginFail();
            throw error;
        }
    }, [accessToken, loginFail, post, refreshToken, setAuthState]);

    const value = useMemo<IOidcContext>(
        () => ({
            loginFail,
            loginMFA,
            loginMFANewCode,
            loginMFARequest,
            loginWithPassword,
            loginWithExternalProvider,
            logout,
            refresh,
            getLoginProviders,
        }),
        [
            loginFail,
            loginMFA,
            loginMFANewCode,
            loginMFARequest,
            loginWithPassword,
            loginWithExternalProvider,
            logout,
            refresh,
            getLoginProviders,
        ]
    );

    return <OidcContext.Provider value={value} {...props} />;
};
